import { useCallback, useMemo, useRef, useState } from 'react';
import { unwrapData } from '@wapharma/shared';
import { orderedDuplicateImportRows } from '@wapharma/shared';
import { apiDownloadBlob, apiPostForm } from '../api';
import { getDisplayError } from '../apiErrors';
import { getToken } from '../auth';

export type ImportRowResult = {
  row: number;
  slug: string;
  productId?: string;
  ok: boolean;
  errors: string[];
};

export type ProvisioningItem = { slug: string; name: string; parentSlug?: string };

export type ImportProvisioning = {
  markesCreated: ProvisioningItem[];
  categoriesCreated: ProvisioningItem[];
  productTypesCreated?: ProvisioningItem[];
  imagesUploaded: Array<{ productSlug: string; source: string; key?: string }>;
  imageFailures: Array<{ productSlug: string; source: string; error?: string }>;
};

export type ImportResult = {
  created: number;
  failed: number;
  results: ImportRowResult[];
  provisioning?: ImportProvisioning;
  offset?: number;
  total?: number;
  processed?: number;
};

export type ImportIssueSeverity = 'error' | 'warning';

export type ImportIssue = {
  code: string;
  severity: ImportIssueSeverity;
  message: string;
  row?: number;
  sheet?: 'products' | 'variants';
  field?: string;
  slug?: string;
};

export type ImportConflictKind = 'duplicate_in_file' | 'exists_in_db';

export type ImportConflictSnapshot = {
  row: number;
  name: string;
  sourceName: string;
  markeSlug?: string | null;
  categorySlug?: string | null;
  subcategorySlug?: string | null;
  retailPrice?: number | null;
  wholesalePrice?: number | null;
  importSourceLine?: string;
};

export type ImportExistingSnapshot = {
  productId: string;
  name: string;
  markeSlug?: string | null;
  categorySlug?: string | null;
  subcategorySlug?: string | null;
  retailPrice?: number | null;
  wholesalePrice?: number | null;
  updatedAt?: string;
};

export type ImportConflict = {
  kind: ImportConflictKind;
  slug: string;
  productRows: number[];
  productSnapshots?: ImportConflictSnapshot[];
  incoming: ImportConflictSnapshot;
  existing?: ImportExistingSnapshot;
};

export type DuplicateRowDisposition = import('@wapharma/shared').DuplicateRowDisposition;

export type ImportConflictResolution = {
  slug: string;
  action: 'skip' | 'replace' | 'keep_row' | 'keep_all';
  keepRow?: number;
  rowDisposition?: Record<number, DuplicateRowDisposition>;
};

export type ImportInferenceStats = {
  total: number;
  with_any_tag: number;
  with_routine: number;
  with_concern: number;
  with_skin: number;
  with_texture: number;
  skipped_empty: number;
  with_taxonomy_hint: number;
  with_type?: number;
  without_type?: number;
  by_routine: Record<string, number>;
  by_concern: Record<string, number>;
  by_type: Record<string, number>;
};

export type ImportPreviewRow = {
  rowNumber: number;
  slug: string;
  name: string;
};

export type ImportInferenceSampleRow = {
  rowNumber: number;
  slug: string;
  name: string;
  tags: string[];
  productTypeCode: string | null;
  categorySlug: string | null;
  categoryName: string | null;
  subcategorySlug: string | null;
  subcategoryName: string | null;
  taxonomyInferenceScore?: number | null;
  taxonomyInferenceSource?: string | null;
  thumbnailRef?: string | null;
};

export type ImportPreview = {
  rowCount: number;
  productsToCreate: number;
  productsAlreadyInDb: number;
  duplicatesInFile: number;
  markesToCreate: number;
  categoriesToCreate: number;
  subcategoriesToCreate: number;
  productTypesUsed: number;
  productTypesToCreate: number;
  variantRows: number;
  productsWithVariants: number;
  /** @deprecated use issues */
  warnings: string[];
  issues: ImportIssue[];
  conflicts: ImportConflict[];
  canImport: boolean;
  importableRowCount: number;
  normalized?: boolean;
  profile?: 'minimal' | 'full';
  normalizeMessage?: string;
  smartImport?: boolean;
  columnMapping?: ImportColumnMapping;
  inference?: ImportInferenceStats;
  inferenceSamples?: ImportInferenceSampleRow[];
  importRows?: ImportPreviewRow[];
};

/** Source header → canonical field (null = ignore). */
export type ImportColumnMapping = Record<string, string | null>;

export type ImportInspectTargetField = {
  key: string;
  label: string;
  required: boolean;
};

export type ImportInspectResult = {
  sheets: { products: true; variants: boolean };
  productHeaders: string[];
  variantHeaders?: string[];
  suggestedMapping: Record<string, string | null>;
  samples: Record<string, string[]>;
  targetFields: ImportInspectTargetField[];
};

export const IMPORT_ACCEPT =
  '.xlsx,.xls,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv';

export const IMPORT_JOB_STORAGE_KEY = 'wapharma-admin-catalog-import-job-id';

type ImportJobStatus = 'PENDING' | 'RUNNING' | 'COMPLETED' | 'FAILED' | 'CANCELLED';

export function isImportFile(file: File): boolean {
  const n = file.name.toLowerCase();
  return n.endsWith('.xlsx') || n.endsWith('.xls') || n.endsWith('.csv');
}

export function defaultConflictResolutions(conflicts: ImportConflict[]): ImportConflictResolution[] {
  return conflicts.map((c) => {
    if (c.kind === 'duplicate_in_file') {
      return { slug: c.slug, action: 'keep_row', keepRow: c.productRows[0] };
    }
    return { slug: c.slug, action: 'skip' };
  });
}

export function computeImportableRowCount(
  preview: ImportPreview,
  resolutions: ImportConflictResolution[],
): number {
  const resBySlug = new Map(resolutions.map((r) => [r.slug, r]));
  let count = preview.rowCount - preview.duplicatesInFile;
  for (const c of preview.conflicts) {
    if (c.kind === 'duplicate_in_file') {
      const importing = orderedDuplicateImportRows(c, resBySlug.get(c.slug));
      count += importing.length - 1;
      continue;
    }
    if (c.kind !== 'exists_in_db') continue;
    const action = resBySlug.get(c.slug)?.action ?? 'skip';
    if (action === 'skip') count -= 1;
  }
  return Math.max(0, count);
}

export function previewHasBlockingErrors(preview: ImportPreview): boolean {
  return preview.issues.some((i) => i.severity === 'error');
}

export async function downloadImportTemplate(
  token: string,
  format: 'xlsx' | 'csv',
  profile: 'full' | 'minimal' = 'full',
) {
  const filename =
    profile === 'minimal'
      ? format === 'csv'
        ? 'modele-import-produits-minimal.csv'
        : 'modele-import-produits-minimal.xlsx'
      : format === 'csv'
        ? 'modele-import-produits.csv'
        : 'modele-import-produits.xlsx';
  await apiDownloadBlob(
    `/catalog/admin/products/import/template?format=${format}&profile=${profile}`,
    token,
    filename,
  );
}

export function useProductBulkImport({
  disabled,
  onImported,
  importOpen: _importOpen,
  onImportOpenChange,
}: {
  disabled?: boolean;
  onImported: () => void;
  importOpen: boolean;
  onImportOpenChange: (open: boolean) => void;
}) {
  const token = getToken();
  const fileRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [inspectLoading, setInspectLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [inspect, setInspect] = useState<ImportInspectResult | null>(null);
  const [columnMapping, setColumnMapping] = useState<ImportColumnMapping | null>(null);
  const [resolutions, setResolutions] = useState<ImportConflictResolution[]>([]);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [pickErr, setPickErr] = useState<string | null>(null);
  const [smartImport, setSmartImportState] = useState(true);
  const [importProgress, setImportProgress] = useState<{ current: number; total: number } | null>(
    null,
  );
  const [activeJobId, setActiveJobId] = useState<string | null>(null);
  const onImportedRef = useRef(onImported);
  onImportedRef.current = onImported;

  const clearStoredJob = useCallback(() => {
    setActiveJobId(null);
    setImportProgress(null);
    setLoading(false);
    try {
      localStorage.removeItem(IMPORT_JOB_STORAGE_KEY);
    } catch {
      /* ignore */
    }
  }, []);

  const reset = useCallback(() => {
    setFile(null);
    setErr(null);
    setPickErr(null);
    setPreview(null);
    setInspect(null);
    setColumnMapping(null);
    setResolutions([]);
    setResult(null);
    setSmartImportState(true);
    clearStoredJob();
    if (fileRef.current) fileRef.current.value = '';
  }, [clearStoredJob]);

  const setSmartImport = useCallback((next: boolean) => {
    setSmartImportState(next);
    setFile(null);
    setErr(null);
    setPickErr(null);
    setPreview(null);
    setInspect(null);
    setColumnMapping(null);
    setResolutions([]);
    setResult(null);
    if (fileRef.current) fileRef.current.value = '';
  }, []);

  /** Job progress tracked by useCatalogImportJobInbox — avoid duplicate /jobs polling. */
  const jobInFlight = false;

  const closeImportPopover = useCallback(() => {
    reset();
    onImportOpenChange(false);
  }, [onImportOpenChange, reset]);

  const runInspect = useCallback(
    async (next: File) => {
      if (!token) return;
      setInspectLoading(true);
      setErr(null);
      setInspect(null);
      setColumnMapping(null);
      setPreview(null);
      setResolutions([]);
      try {
        const fd = new FormData();
        fd.append('file', next);
        fd.append('smartImport', smartImport ? 'true' : 'false');
        const raw = await apiPostForm<{ data: ImportInspectResult } | ImportInspectResult>(
          '/catalog/admin/products/import/inspect',
          fd,
          token,
        );
        const data = unwrapData(raw) as ImportInspectResult;
        setInspect(data);
        setColumnMapping({ ...data.suggestedMapping });
      } catch (e: unknown) {
        setErr(getDisplayError(e));
        setInspect(null);
        setColumnMapping(null);
        setFile(null);
      } finally {
        setInspectLoading(false);
      }
    },
    [token, smartImport],
  );

  const runPreview = useCallback(
    async (
      next: File,
      mapping: ImportColumnMapping,
      nextResolutions?: ImportConflictResolution[],
    ) => {
      if (!token) return;
      setPreviewLoading(true);
      setErr(null);
      try {
        const fd = new FormData();
        fd.append('file', next);
        fd.append('smartImport', smartImport ? 'true' : 'false');
        fd.append('columnMapping', JSON.stringify(mapping));
        if (nextResolutions?.length) {
          fd.append('resolutions', JSON.stringify(nextResolutions));
        }
        const raw = await apiPostForm<{ data: ImportPreview } | ImportPreview>(
          '/catalog/admin/products/import/preview',
          fd,
          token,
        );
        const data = unwrapData(raw) as ImportPreview;
        setPreview(data);
        setResolutions(nextResolutions ?? defaultConflictResolutions(data.conflicts ?? []));
      } catch (e: unknown) {
        setErr(getDisplayError(e));
        setPreview(null);
        setResolutions([]);
      } finally {
        setPreviewLoading(false);
      }
    },
    [token, smartImport],
  );

  const applyFile = useCallback(
    (next: File | null) => {
      if (!next) {
        setFile(null);
        setInspect(null);
        setColumnMapping(null);
        setPreview(null);
        setResolutions([]);
        return;
      }
      if (!isImportFile(next)) {
        setPickErr('Format non pris en charge. Utilisez un fichier .xlsx ou .csv.');
        return;
      }
      setPickErr(null);
      setResult(null);
      setErr(null);
      setPreview(null);
      setResolutions([]);
      setFile(next);
      void runInspect(next);
    },
    [runInspect],
  );

  const confirmMapping = useCallback(
    async (mapping: ImportColumnMapping) => {
      if (!file) return;
      setColumnMapping(mapping);
      await runPreview(file, mapping);
    },
    [file, runPreview],
  );

  const clearPreviewKeepMapping = useCallback(() => {
    setPreview(null);
    setResolutions([]);
    setErr(null);
  }, []);

  const refreshPreview = useCallback(
    async (nextResolutions?: ImportConflictResolution[]) => {
      if (!file || !columnMapping) return;
      await runPreview(file, columnMapping, nextResolutions ?? resolutions);
    },
    [file, columnMapping, resolutions, runPreview],
  );

  const setConflictResolutions = useCallback((next: ImportConflictResolution[]) => {
    setResolutions(next);
  }, []);

  const importableRowCount = useMemo(() => {
    if (!preview) return 0;
    return computeImportableRowCount(preview, resolutions);
  }, [preview, resolutions]);

  const previewForDisplay = useMemo(() => {
    if (!preview) return null;
    return { ...preview, productsToCreate: importableRowCount };
  }, [preview, importableRowCount]);

  const runImport = async () => {
    if (!token || !file || !preview || !columnMapping) return;
    setLoading(true);
    setErr(null);
    setResult(null);
    setImportProgress({ current: 0, total: importableRowCount });

    try {
      if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
        void Notification.requestPermission();
      }
      const fd = new FormData();
      fd.append('file', file);
      fd.append('smartImport', smartImport ? 'true' : 'false');
      fd.append('columnMapping', JSON.stringify(columnMapping));
      if (resolutions.length) {
        fd.append('resolutions', JSON.stringify(resolutions));
      }
      const raw = await apiPostForm<
        | { data: { id: string; status: ImportJobStatus; totalRows: number } }
        | { id: string; status: ImportJobStatus; totalRows: number }
      >('/catalog/admin/products/import/jobs', fd, token);
      const started = unwrapData(raw) as { id: string; status: ImportJobStatus; totalRows: number };
      setActiveJobId(started.id);
      setImportProgress({ current: 0, total: started.totalRows });
      try {
        localStorage.setItem(IMPORT_JOB_STORAGE_KEY, started.id);
      } catch {
        /* ignore */
      }
      window.dispatchEvent(new Event('wapharma-import-job-started'));
      setLoading(false);
      setFile(null);
      setPreview(null);
      setInspect(null);
      setColumnMapping(null);
      setResolutions([]);
      if (fileRef.current) fileRef.current.value = '';
      onImportOpenChange(false);
    } catch (e: unknown) {
      setErr(getDisplayError(e));
      setLoading(false);
      setImportProgress(null);
    }
  };

  const zoneDisabled =
    !token || disabled || previewLoading || inspectLoading || (loading && jobInFlight);
  const hasBlockingErrors = preview != null && previewHasBlockingErrors(preview);
  const canImport = Boolean(
    file &&
      preview &&
      columnMapping &&
      !previewLoading &&
      !hasBlockingErrors &&
      importableRowCount > 0 &&
      resolutions.length >= (preview.conflicts?.length ?? 0),
  );

  const mappingReady = Boolean(file && inspect && columnMapping && !preview);

  return {
    fileRef,
    file,
    loading,
    previewLoading,
    inspectLoading,
    err,
    preview: previewForDisplay,
    inspect,
    columnMapping,
    setColumnMapping,
    confirmMapping,
    clearPreviewKeepMapping,
    mappingReady,
    resolutions,
    setConflictResolutions,
    result,
    pickErr,
    importProgress,
    activeJobId,
    jobStatus: null as ImportJobStatus | null,
    jobCreatedCount: 0,
    jobInFlight,
    zoneDisabled,
    canImport,
    token,
    smartImport,
    setSmartImport,
    reset,
    closeImportPopover,
    applyFile,
    refreshPreview,
    runImport,
    onImportPopoverChange: (next: boolean) => {
      if (!next) {
        if (!jobInFlight) reset();
        onImportOpenChange(false);
        return;
      }
      onImportOpenChange(true);
    },
  };
}
